import { useNavigate, useLocation } from "react-router-dom";
import { useContext, useState, useEffect, useRef } from "react";
import EventSuggestions from "./EventSuggestions";
import { GlobalContext } from "../context/GlobalContext";
import FilterModal from "../components/FilterModal";
import Hosts from "./Hosts";
import EventRequests from "./EventRequests";
import { VIEW_MODES } from "./list/constants";
import { EventsHeader, EventsTabs } from "./list/EventsToolbar";
import { EventsCardView, EventsTableView, EmptyState, LoadingIndicator, EndOfResults } from "./list/EventsListViews";
import { EventPreviewPanel, HealthCheckModal } from "./list/EventsModals";
import { useEvents, useInfiniteScroll, useTabCounts } from "./list/useEventsData";
import { FinishCreatingSection } from "./list/FinishCreatingSection";
import { NextEventsPlanSection } from "./list/NextEventsPlanSection";
import { capture, EVENTS } from "../analytics";

// Tabs a host can reach. `hosts` and `requests` are super-admin-only (App.js
// wraps both in PrivateAdminRoute), so they are excluded here for the same
// reason analytics/routes.js excludes their routes — operator sweeps would
// dominate any per-tab count they appeared in.
const HOST_TABS = ["upcoming", "completed", "cancelled", "suggestions"];

export default function Events({ tab = "upcoming" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { globalState } = useContext(GlobalContext);
  const [viewMode, setViewMode] = useState(VIEW_MODES.CARD);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showHealthModal, setShowHealthModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    hostname: '',
    eventName: '',
    startDate: '',
    endDate: ''
  });

  // Determine active tab from route or prop
  const activeTab = tab || (location.pathname.includes('/completed') ? 'completed' : 'upcoming');
  const isListTab = activeTab === "upcoming" || activeTab === "completed" || activeTab === "cancelled";

  const { events, loading, hasMore, loadingMore, fetchEvents } = useEvents(globalState, filters, activeTab);
  const observerRef = useInfiniteScroll(fetchEvents, hasMore, loadingMore, loading);
  const tabCounts = useTabCounts(globalState);

  // Once per settled load, per tab — matching the PWA's `event_list_viewed`
  // rule. `loading` goes true on every infinite-scroll page, so the ref is
  // keyed by tab rather than being a plain boolean: switching tabs re-arms it,
  // scrolling does not, and a host who pages through forty events still counts
  // as one list view.
  const listViewedRef = useRef(null);
  useEffect(() => {
    if (loading || !HOST_TABS.includes(activeTab)) return;
    if (listViewedRef.current === activeTab) return;
    listViewedRef.current = activeTab;
    capture(EVENTS.EVENT_LIST_VIEWED, {
      tab: activeTab,
      resultCount: events.length,
      // Whether the host narrowed the list at all. The filter VALUES are not
      // sent: `hostname` and `eventName` are free text a host types, and the
      // sanitizer drops `hostname` outright anyway.
      isFiltered: Object.values(filters).some((v) => v !== "" && v != null),
    });
  }, [loading, activeTab, events.length, filters]);

  const filteredEvents = events
    .filter(e => {
      if (activeTab === "cancelled") return e.status === "cancelled";
      if (activeTab === "completed") return e.isCompleted && e.status !== "cancelled";
      return !e.isCompleted && e.status !== "cancelled"; // upcoming
    })
    .sort((a, b) => {
      if (activeTab === "completed" || activeTab === "cancelled") {
        return b.date.toDate() - a.date.toDate(); // latest to oldest
      }
      return 0; // keep original order for upcoming
    });

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <EventsHeader
        viewMode={viewMode}
        setViewMode={setViewMode}
        setShowFilterModal={setShowFilterModal}
        setShowHealthModal={setShowHealthModal}
        navigate={navigate}
        activeTab={activeTab}
        isListTab={isListTab}
        isSuperAdmin={globalState.isSuperAdmin}
      />

      <EventsTabs activeTab={activeTab} navigate={navigate} isSuperAdmin={globalState.isSuperAdmin} counts={tabCounts} />

      {activeTab === "suggestions" ? (
        <EventSuggestions />
      ) : activeTab === "hosts" ? (
        <Hosts embedded />
      ) : activeTab === "requests" ? (
        <EventRequests embedded />
      ) : (
        <>
          {activeTab === "upcoming" && (
            <FinishCreatingSection vendorID={globalState.vendorID} isSuperAdmin={globalState.isSuperAdmin} />
          )}
          {activeTab === "upcoming" && <NextEventsPlanSection vendorID={globalState.vendorID} />}
          {viewMode === VIEW_MODES.CARD ? (
            <EventsCardView events={filteredEvents} loading={loading} onSelectEvent={setSelectedEvent} isSuperAdmin={globalState.isSuperAdmin} navigate={navigate} />
          ) : (
            <EventsTableView events={filteredEvents} navigate={navigate} onSelectEvent={setSelectedEvent} isSuperAdmin={globalState.isSuperAdmin} />
          )}
          {filteredEvents.length === 0 && !loading && (
            <EmptyState navigate={navigate} activeTab={activeTab} />
          )}

          {/* Infinite Scroll Trigger */}
          {hasMore && (
            <LoadingIndicator loadingMore={loadingMore} observerRef={observerRef} />
          )}

          {/* End of Results Indicator */}
          {!hasMore && events.length > 0 && (
            <EndOfResults />
          )}

          <FilterModal
            show={showFilterModal}
            onClose={() => setShowFilterModal(false)}
            onApply={(newFilters) => {
              setFilters(newFilters);
            }}
            events={events}
          />

          <EventPreviewPanel
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
          />
          <HealthCheckModal
            open={showHealthModal}
            onClose={() => setShowHealthModal(false)}
          />
        </>
      )}
    </div>
  );
}
